import React from 'react';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {useCancelTaskTemplate, useDeleteTaskInstance} from 'app/tasks/api';
import {repeats} from 'app/tasks/utils';
import {gt} from 'app/utils/i18n';

import {
  SlimTaskInstanceSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

interface Props {
  show: boolean;
  task: TaskInstanceSerializedType | SlimTaskInstanceSerializedType;
  close: () => void;
  onClosed: () => void;
}

export function CancelTaskModal({
  show,
  task,
  close,
  onClosed,
}: Props): JSX.Element | null {
  const [cancelSingle, setCancelSingle] = React.useState(true);

  const [cancelTaskTemplate, {isLoading: isCanceling}] =
    useCancelTaskTemplate();
  const [deleteTaskInstance, {isLoading: isDeleting}] = useDeleteTaskInstance();

  const isBusy = isCanceling || isDeleting;

  const isSingle = task != null ? !repeats(task.template.rrule) : false;

  function performCancel(): Promise<unknown> {
    return (
      cancelSingle
        ? deleteTaskInstance(task.url)
        : cancelTaskTemplate({
            url: task.template.url,
            rrule: task.template.rrule,
            until: task.due,
          }).unwrap()
    ).then((r) => {
      close();
      return r;
    });
  }

  return task == null ? null : (
    <TabbedModal
      show={show}
      onCancel={close}
      onClosed={onClosed}
      tabs={[
        {
          eventKey: 'main-tab',
          name: gt('Cancel Task'),
          icon: 'fa fa-user',
          content: (
            <div>
              {isSingle ? (
                <p>{gt('Are you sure you want to cancel this task?')}</p>
              ) : (
                <div>
                  {' '}
                  <p>
                    {gt(
                      'Would you like to cancel just this one task, or also the following tasks in this series?'
                    )}
                  </p>
                  <div className="mx-5 mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        id="cancel-task-modal-cancel-instance-radio"
                        type="radio"
                        onChange={() => setCancelSingle(true)}
                        checked={cancelSingle}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="cancel-task-modal-cancel-instance-radio"
                      >
                        {gt('Cancel only this task')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        id="cancel-task-modal-cancel-series-radio"
                        type="radio"
                        onChange={() => setCancelSingle(false)}
                        checked={!cancelSingle}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="cancel-task-modal-cancel-series-radio"
                      >
                        {gt('Cancel this task and following ones')}
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <StyledModalFooter>
                {!isBusy ? (
                  <span>
                    <button className="btn btn-link" onClick={close}>
                      {gt('Back')}
                    </button>{' '}
                    <button
                      className="btn btn-primary"
                      onClick={() => performCancel()}
                    >
                      {gt('Confirm')}
                    </button>
                  </span>
                ) : (
                  <button className="btn btn-primary" disabled>
                    <i className="fa fa-spinner fa fa-spin" /> {gt('canceling')}
                  </button>
                )}
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
}
